/**
 * Simple logging utility for website
 * Uses an earlier approach instead of console.log
 */

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.logLevels = {
      ERROR: 0,
      WARN: 1,
      INFO: 2,
      DEBUG: 3
    };
    // Set to ERROR only to minimize console output
    this.currentLevel = this.logLevels.ERROR;
  }

  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    if (data) {
      return `${prefix} ${message} ${JSON.stringify(data)}`;
    }
    return `${prefix} ${message}`;
  }

  shouldLog(level) {
    return this.logLevels[level] <= this.currentLevel;
  }

  error(message, data = null) {
    if (this.shouldLog('ERROR')) {
      const formattedMessage = this.formatMessage('ERROR', message, data);
      if (this.isDevelopment) {
        console.error(formattedMessage);
      } else {
        // In production, you might want to send to a logging service
        this.sendToLoggingService('ERROR', message, data);
      }
    }
  }

  warn(message, data = null) {
    if (this.shouldLog('WARN')) {
      const formattedMessage = this.formatMessage('WARN', message, data);
      if (this.isDevelopment) {
        console.warn(formattedMessage);
      } else {
        this.sendToLoggingService('WARN', message, data);
      }
    }
  }

  info(message, data = null) {
    if (this.shouldLog('INFO')) {
      const formattedMessage = this.formatMessage('INFO', message, data);
      if (this.isDevelopment) {
        console.info(formattedMessage);
      } else {
        this.sendToLoggingService('INFO', message, data);
      }
    }
  }

  debug(message, data = null) {
    if (this.shouldLog('DEBUG')) {
      const formattedMessage = this.formatMessage('DEBUG', message, data);
      if (this.isDevelopment) {
        // console.log(formattedMessage);
      } else {
        this.sendToLoggingService('DEBUG', message, data);
      }
    }
  }

  // Method to send logs to external service in production
  sendToLoggingService(level, message, data) {
    // This can be implemented to send logs to a logging service
    // For now, we'll just store in localStorage for debugging
    try {
      const logs = JSON.parse(localStorage.getItem('website_logs') || '[]');
      logs.push({
        timestamp: new Date().toISOString(),
        level,
        message,
        data,
        url: window.location.href,
        userAgent: navigator.userAgent
      });
      
      // Keep only last 100 logs
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }
      
      localStorage.setItem('website_logs', JSON.stringify(logs));
    } catch (error) {
      // Fallback to console if localStorage fails
      console.error('Failed to store log:', error);
    }
  }

  // Method to get stored logs
  getStoredLogs() {
    try {
      return JSON.parse(localStorage.getItem('website_logs') || '[]');
    } catch (error) {
      return [];
    }
  }

  // Method to clear stored logs
  clearStoredLogs() {
    localStorage.removeItem('website_logs');
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;
